import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@repo/ui/src/lib/utils';
import { NAV_GROUPS, isNavItemActive, splitMobileNav } from '../../nav/navConfig';
import { useAuthStore } from '../../stores/authStore';
import { useBottomBarStore } from '../../stores/bottomBarStore';
import { useKeyboardOpen } from '../../hooks/useKeyboardOpen';
import { useAttentionItems } from '../../hooks/useAttentionItems';

/**
 * Phone bottom tab bar (`md:hidden`): the four daily nav items as tabs, plus
 * a fifth "Thêm" tab opening `MoreSheet` for everything else.
 *
 * Renders nothing — not just visually, but from the DOM — while a bottom bar
 * (`BulkActionsBar`, `MoveModeBar`) claims the bottom edge, or while the
 * on-screen keyboard is up (both hooks are read before this early return, per
 * the rules of hooks). `useAttentionItems` returns only billing signals for a
 * role that cannot bill (its THI-105 monthly-review row is not gated the same
 * way — see that hook), so the Hoá đơn dot below counts everything BUT that
 * row: a review nudge lighting up the invoices tab would misdirect a MEMBER
 * straight past the review it's actually about.
 */
export function BottomTabBar({ moreOpen, onOpenMore }: { moreOpen: boolean; onOpenMore: () => void }) {
  const { t } = useTranslation('common');
  const location = useLocation();
  const role = useAuthStore((s) => s.role);
  const { primary, more } = useMemo(() => splitMobileNav(NAV_GROUPS, role), [role]);
  const occupied = useBottomBarStore((s) => s.occupants.size > 0);
  const keyboard = useKeyboardOpen();
  const { items } = useAttentionItems();
  const billingItemCount = items.filter((i) => i.kind !== 'monthlyReview').length;

  if (occupied || keyboard) return null;

  const moreActive = more.some((i) => isNavItemActive(i.href, location.pathname));

  return (
    <nav
      aria-label={t('nav.mainNav')}
      className="md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-surface-200 bg-surface-0/95 pb-[env(safe-area-inset-bottom)] backdrop-blur"
    >
      <ul className="flex h-16 items-stretch">
        {primary.map((item) => {
          const active = isNavItemActive(item.href, location.pathname);
          const dot = item.key === 'invoices' && billingItemCount > 0;
          return (
            <li key={item.key} className="flex-1">
              <Link
                to={item.href}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex h-full min-h-11 flex-col items-center justify-center gap-1 text-[11px]',
                  active ? 'font-semibold text-brand-600' : 'text-surface-500',
                )}
              >
                <span className="relative">
                  <item.icon className="size-5" strokeWidth={active ? 2 : 1.8} />
                  {dot && <span aria-hidden className="absolute -right-1 -top-1 size-2 rounded-full bg-attention" />}
                </span>
                {/* `mobileLabel` when the item was designed as a tab; `label`
                    when `splitMobileNav` promoted it to fill an empty slot. */}
                {t((item.mobileLabel ?? item.label).key, { ns: (item.mobileLabel ?? item.label).ns })}
              </Link>
            </li>
          );
        })}
        <li className="flex-1">
          <button
            type="button"
            onClick={onOpenMore}
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            className={cn(
              'flex h-full w-full min-h-11 flex-col items-center justify-center gap-1 text-[11px]',
              moreActive ? 'font-semibold text-brand-600' : 'text-surface-500',
            )}
          >
            <MoreHorizontal className="size-5" strokeWidth={moreActive ? 2 : 1.8} />
            {t('nav.tab.more')}
          </button>
        </li>
      </ul>
    </nav>
  );
}
